// Global Instructions Rule Applied!
import React, { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Input, List, Avatar, Empty, Spin, Typography } from 'antd'
import { debounce } from 'lodash'
import { getAccessToken } from '../../../auth/authService'
import { store } from '../../../../state_manager/store.ts'
import { searchGlobal, GlobalSearchResult } from '../../../services/utilitiesService.ts'
import {
  GLOBAL_SEARCH_DEBOUNCE_MS,
  createEmptyGlobalSearchResults,
  hasAnyGlobalSearchResults,
  GlobalSearchResultsByBucket,
  GlobalSearchBucketKey
} from '../../../config/globalSearch.ts'
import { detailPath, avatarInitials } from '../lib/routes.ts'

/**
 * Buckets returned by the global-search RPC, with their display label + route module,
 * in display order. Consolidated funds are left out: the mobile companion has no
 * consolidated fund screen.
 */
const BUCKET_META: { key: GlobalSearchBucketKey; label: string; module: string }[] = [
  { key: 'funds', label: 'Funds', module: 'funds' },
  { key: 'accounts', label: 'Companies', module: 'companies' },
  { key: 'investors', label: 'Investors', module: 'companies' },
  { key: 'deals', label: 'Deals', module: 'deals' },
  { key: 'contacts', label: 'Contacts', module: 'contacts' }
]

const MIN_QUERY_LENGTH = 2

/**
 * Global search — the mobile home. Reuses `searchGlobal` (same backend RPC the desktop
 * navbar search uses) and the shared bucket config. Tapping a result deep-links to the
 * matching mobile detail screen.
 */
export function SearchScreen(): React.JSX.Element {
  const navigate = useNavigate()
  const [value, setValue] = useState('')
  const [results, setResults] = useState<GlobalSearchResultsByBucket>(() => createEmptyGlobalSearchResults())
  const [loading, setLoading] = useState(false)
  const [searched, setSearched] = useState(false)

  const performSearch = useCallback(async (query: string) => {
    const trimmed = query.trim()
    if (!trimmed) {
      setResults(createEmptyGlobalSearchResults())
      setSearched(false)
      return
    }
    setLoading(true)
    setSearched(true)
    const token = getAccessToken(store.getState().auth)
    const res = await searchGlobal(trimmed, token)
    setLoading(false)
    setResults(res.success && res.data ? res.data : createEmptyGlobalSearchResults())
  }, [])

  const debouncedSearch = useMemo(
    () =>
      debounce((query: string) => {
        if (query && query.trim().length >= MIN_QUERY_LENGTH) performSearch(query)
      }, GLOBAL_SEARCH_DEBOUNCE_MS),
    [performSearch]
  )

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = e.target.value
      setValue(next)
      if (!next || next.trim().length < MIN_QUERY_LENGTH) {
        debouncedSearch.cancel()
        setResults(createEmptyGlobalSearchResults())
        setSearched(false)
      } else {
        debouncedSearch(next)
      }
    },
    [debouncedSearch]
  )

  const hasResults = hasAnyGlobalSearchResults(results)

  // Layout: a single scroll surface (the shell's <main>). The search box sticks to the TOP,
  // so on iOS the focus target is already above the keyboard and the OS never shifts the
  // viewport; results flow below and scroll in <main> like every other screen.
  return (
    <div>
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          background: '#fff',
          padding: 12,
          borderBottom: '1px solid #f0f0f0'
        }}
      >
        <Input.Search
          autoFocus
          allowClear
          enterButton
          size="large"
          placeholder="Search companies, deals, contacts, funds"
          value={value}
          onChange={onChange}
          onSearch={(query) => {
            debouncedSearch.cancel()
            performSearch(query)
          }}
        />
      </div>

      <div style={{ padding: 12 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <Spin />
          </div>
        ) : hasResults ? (
          BUCKET_META.map(({ key, label, module }) => {
            const items = results[key]
            if (!items || items.length === 0) return null
            return (
              <div key={key} style={{ marginBottom: 16 }}>
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4, paddingLeft: 4 }}
                >
                  {label}
                </Typography.Text>
                <List
                  size="small"
                  dataSource={items}
                  rowKey={(item: GlobalSearchResult) => `${item.entity_type}-${item.entity_id}`}
                  renderItem={(item: GlobalSearchResult) => (
                    <List.Item
                      onClick={() => navigate(detailPath(module, item.entity_id))}
                      style={{
                        cursor: 'pointer',
                        background: '#fff',
                        borderRadius: 8,
                        marginTop: 6,
                        paddingLeft: 12,
                        paddingRight: 12
                      }}
                    >
                      <List.Item.Meta
                        avatar={
                          <Avatar style={{ backgroundColor: '#1890ff', color: '#fff', fontSize: 12 }}>
                            {avatarInitials(item.title)}
                          </Avatar>
                        }
                        title={item.title}
                        description={item.subtitle || undefined}
                      />
                    </List.Item>
                  )}
                />
              </div>
            )
          })
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={searched ? 'No results found' : 'Type to search'}
            style={{ marginTop: 48 }}
          />
        )}
      </div>
    </div>
  )
}

export default SearchScreen
